/**
 * Hybrid Tooltip content
 * Automatically chooses between Tailwind and styled-components based on props.
 *
 * Rendered as the base-ui Tooltip Popup's `render` element, so it must forward
 * the ref and all base-ui-injected props (id, role, aria-*, data-state,
 * positioning style, className) onto its chosen leaf element.
 */

import React from "react";
import type { TypeBoxProps } from "@sproutsocial/seeds-react-box";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import { StyledTooltipContent } from "./styles"; // Styled-components version
import { TooltipTailwind } from "./TooltipTailwind"; // Tailwind version

export interface TypeTooltipHybridProps extends Omit<TypeBoxProps, "children"> {
  appearance?: "pill" | "box";
  children?: React.ReactNode;
}

// Box style props NOT covered by `needsStyledComponents`. `TypeTooltipProps`
// extends `Omit<TypeBoxProps, ...>`, so these are part of the public API; if a
// consumer passes one it must route to the styled-components (Box) path rather
// than spread onto the plain Tailwind <div> (which would drop the styling and
// emit React "unknown DOM attribute" warnings). `StyledTooltipContent` is a
// `styled(Box)`, so it accepts all of these safely.
const ADDITIONAL_BOX_STYLE_PROPS = [
  "borderRadius",
  "border",
  "borderColor",
  "borderWidth",
  "borderStyle",
  "boxShadow",
  "fontSize",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "fontStyle",
  "textAlign",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "zIndex",
  "opacity",
  "size",
] as const;

const hasAdditionalBoxStyleProps = (props: Record<string, any>): boolean =>
  ADDITIONAL_BOX_STYLE_PROPS.some((prop) => props[prop] !== undefined);

const TooltipHybrid = React.forwardRef<HTMLDivElement, TypeTooltipHybridProps>(
  ({ appearance, ...rest }, ref) => {
    if (needsStyledComponents(rest) || hasAdditionalBoxStyleProps(rest)) {
      // Styled-components path: a consumer passed system props (e.g. bg, mt) or a
      // styled(Tooltip) extension injected theme/forwardedComponent. The inline
      // system props below reproduce the original StyledTooltipContent styling.
      return (
        <StyledTooltipContent
          ref={ref}
          role="tooltip"
          appearance={appearance}
          borderRadius={appearance === "box" ? 500 : "5000em"}
          px={400}
          py={appearance === "box" ? 400 : 200}
          m={200}
          color="text.body"
          bg="container.background.base"
          boxShadow="medium"
          border={500}
          borderColor="container.border.base"
          {...rest}
        />
      );
    }

    // Tailwind path (preferred - better performance). The gate above routes any
    // Box system/style prop (every prop enumerated in `needsStyledComponents`
    // plus `ADDITIONAL_BOX_STYLE_PROPS`) to the styled-components branch, so only
    // DOM-safe props (id, role, aria-*, data-*, style, className, tabIndex, ref)
    // remain in `rest` here.
    return (
      <TooltipTailwind
        ref={ref}
        appearance={appearance}
        {...(rest as React.HTMLAttributes<HTMLDivElement>)}
      />
    );
  }
);

TooltipHybrid.displayName = "TooltipHybrid";
export default TooltipHybrid;
